import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../i18n';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAdvertiser } from '../contexts/AdvertiserContext';
import { api } from '../services/api';
import { formatCurrency } from '../utils/currency';
import CurrencyValue from './CurrencyValue';
import NewCampaignButton from './NewCampaignButton';
import SenseMaxButton from './SenseMaxButton';
import { getBranding } from '../config/branding';
import '../styles/dashboard.css';

interface DashboardStats {
  spend: number;
  spendPrev: number;
  campaignsCount: number;
  campaignsCountPrev: number;
  conversions: number;
  conversionsPrev: number;
  clicks: number;
  clicksPrev: number;
  currency?: string;
  // Admin-only fields
  earn?: number;
  earnPrev?: number;
  profit?: number;
  profitPrev?: number;
  clientsCount?: number;
  clientsCountPrev?: number;
}

interface ActiveCampaign {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'ended';
  daysRemaining: number;
  spend: number;
  budget: number;
  impressions: number;
  clicks: number;
  ctr: number;
  cpc: number;
  conversions: number;
  budget_currency?: string | null;
  has_external_data?: boolean;
}

interface TrendPoint {
  date: string;
  impressions: number;
  clicks: number;
  conversions: number;
  spend: number;
}

type ChartMetric = 'spend' | 'impressions' | 'clicks';

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toLocaleString('ru-RU');
};

// Remove timestamp suffix from campaign name (_1234567890)
const cleanTimestampSuffix = (name: string): string => {
  if (!name) return name;
  return name.replace(/_\d{10}$/, '');
};

const calculateChange = (current: number, previous: number): number => {
  if (previous === 0) return 0;
  return ((current - previous) / previous) * 100;
};

function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAnalyst = user?.role === 'analyst' || user?.role === 'agency_analyst';
  const { selectedAdvertiserId, selectedAdvertiser, isAllClients } = useAdvertiser();
  const [currency, setCurrency] = useState(user?.currency || 'EUR');
  const [stats, setStats] = useState<DashboardStats>({
    spend: 0,
    spendPrev: 0,
    campaignsCount: 0,
    campaignsCountPrev: 0,
    conversions: 0,
    conversionsPrev: 0,
    clicks: 0,
    clicksPrev: 0,
  });
  const [activeCampaigns, setActiveCampaigns] = useState<ActiveCampaign[]>([]);
  const [trendData, setTrendData] = useState<TrendPoint[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<ChartMetric>('spend');
  const [loading, setLoading] = useState(true);

  const loadDashboardData = useCallback(async () => {
    try {
      const advParam = !isAllClients ? `advertiser_id=${selectedAdvertiserId}` : '';
      const sep = (url: string) => url.includes('?') ? '&' : '?';

      const [statsResponse, campaignsResponse, trendResponse] = await Promise.all([
        api.get(`/api/reports/dashboard/stats${advParam ? `?${advParam}` : ''}`),
        api.get(`/api/reports/campaigns/list${advParam ? `?${advParam}` : ''}`),
        api.get(`/api/reports/dashboard/trend?days=30${advParam ? `&${advParam}` : ''}`)
      ]);

      setStats(statsResponse.data);
      if (statsResponse.data.currency) setCurrency(statsResponse.data.currency);

      const campaigns = campaignsResponse.data.campaigns;
      const activeCampaignsList = campaigns.filter((c: any) => c.status === 'active');

      // Set active campaigns (top 2 by spend)
      const top2 = activeCampaignsList
        .sort((a: any, b: any) => (b.spend || 0) - (a.spend || 0))
        .slice(0, 2);

      // A Sense Max campaign is a UNIFIED cross-platform campaign: its /campaigns/list row
      // is only the Sense (DV) leg (name, 35% budget, Sense metrics). Pull the Max budget
      // breakdown so the card shows the FULL client budget and SUMMED metrics (Sense + external),
      // matching the campaigns list — otherwise the dashboard understates budget (10.5k vs 30k).
      const maxBudget: Record<string, any> = {};
      await Promise.all(top2.filter((c: any) => c.sense_max).map(async (c: any) => {
        try {
          const r = await api.get(`/api/sense-max/campaign/${c.id}/budget`);
          maxBudget[c.id] = r.data;
        } catch { /* leg-only fallback below */ }
      }));

      const activeCampaignsData: ActiveCampaign[] = top2.map((c: any) => {
          const endDate = c.end_date ? new Date(c.end_date) : null;
          const today = new Date();
          const daysRemaining = endDate
            ? Math.max(0, Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)))
            : 0;

          const bd = maxBudget[c.id];
          const isMax = !!c.sense_max && !!bd;
          const spend = (c.spend || 0) + (isMax ? (bd.external_spend || 0) : 0);
          const impressions = (c.impressions || 0) + (isMax ? (bd.external_impressions || 0) : 0);
          const clicks = (c.clicks || 0) + (isMax ? (bd.external_clicks || 0) : 0);
          const budget = isMax ? (bd.total_budget ?? c.budget ?? spend * 1.1) : (c.budget || c.spend * 1.1);
          const ctr = impressions > 0 ? (clicks / impressions) * 100 : (c.ctr || 0);
          const cpc = clicks > 0 ? spend / clicks : 0;

          return {
            id: c.id,
            name: c.name,
            status: c.status,
            daysRemaining,
            spend,
            budget,
            impressions,
            clicks,
            ctr,
            cpc,
            conversions: c.conversions || 0,
            budget_currency: c.budget_currency || (isMax ? bd.currency : null) || null,
            has_external_data: c.has_external_data || false,
          };
        });

      setActiveCampaigns(activeCampaignsData);

      // Use trend data from dedicated endpoint
      const trendData = trendResponse.data.trend.map((t: any) => ({
        date: t.date,
        impressions: t.impressions || 0,
        clicks: t.clicks || 0,
        conversions: 0,
        spend: t.spend || 0,
      }));

      setTrendData(trendData);

    } catch (error) {
      console.error(t('dashboard.loadFailed'), error);
    } finally {
      setLoading(false);
    }
  }, [isAllClients, selectedAdvertiserId]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Interactive Dashboard Chart Component
  const DashboardChart: React.FC<{ data: TrendPoint[], metric: ChartMetric }> = ({ data, metric }) => {
    const { t } = useTranslation();
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const svgRef = useRef<SVGSVGElement>(null);

    const metricLabels: Record<ChartMetric, string> = {
      spend: '',
      impressions: 'impressions',
      clicks: 'clicks'
    };

    if (!data || data.length === 0) {
      return <div className="chart-line"></div>;
    }

    const width = 1000;
    const height = 280;
    const paddingX = 10;
    const paddingY = 30;

    const getValue = (d: TrendPoint) => {
      switch (metric) {
        case 'spend': return d.spend;
        case 'impressions': return d.impressions;
        case 'clicks': return d.clicks;
        default: return d.spend;
      }
    };

    const values = data.map(getValue);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;

    const getX = (index: number) => paddingX + (index / Math.max(data.length - 1, 1)) * (width - paddingX * 2);
    const getY = (value: number) => height - paddingY - ((value - min) / range) * (height - paddingY * 2);
    const avgY = getY(avg);

    // Create smooth bezier curve (Catmull-Rom to Bezier)
    const createSmoothPath = () => {
      if (data.length < 2) {
        const x = getX(0);
        const y = getY(getValue(data[0]));
        return `M ${x} ${y}`;
      }

      const pts = data.map((d, i) => ({ x: getX(i), y: getY(getValue(d)) }));

      if (pts.length === 2) {
        return `M ${pts[0].x} ${pts[0].y} L ${pts[1].x} ${pts[1].y}`;
      }

      let path = `M ${pts[0].x} ${pts[0].y}`;

      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[Math.max(0, i - 1)];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[Math.min(pts.length - 1, i + 2)];

        const tension = 6;
        const cp1x = p1.x + (p2.x - p0.x) / tension;
        const cp1y = p1.y + (p2.y - p0.y) / tension;
        const cp2x = p2.x - (p3.x - p1.x) / tension;
        const cp2y = p2.y - (p3.y - p1.y) / tension;

        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
      }

      return path;
    };

    const smoothPath = createSmoothPath();

    // Brand gradient colors (orange to purple)
    const strokeColor = '#FF6B4A';
    const strokeColorEnd = '#8E3A9A';
    const gradientId = `dashboard-gradient-${metric}`;

    // Area fill path - extend to full height for proper gradient fade
    const areaPath = `${smoothPath} L ${getX(data.length - 1)} ${height} L ${paddingX} ${height} Z`;

    const lastIndex = data.length - 1;
    const lastX = getX(lastIndex);
    const lastY = getY(getValue(data[lastIndex]));

    const formatTooltipDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
    };

    const formatTooltipValue = (value: number) => {
      if (metric === 'spend') return formatCurrency(value, currency);
      if (metric === 'clicks') return Math.round(value).toLocaleString('ru-RU');
      return formatNumber(value);
    };

    const formatAvgValue = (value: number) => {
      if (metric === 'spend') return formatCurrency(value, currency);
      if (metric === 'clicks') return Math.round(value).toLocaleString('ru-RU');
      return formatNumber(Math.round(value));
    };

    const getChange = (index: number) => {
      if (index === 0) return null;
      const prev = getValue(data[index - 1]);
      const curr = getValue(data[index]);
      if (prev === 0) return null;
      return ((curr - prev) / prev) * 100;
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentX = x / rect.width;

      // Find closest data point based on percentage position
      const closestIndex = Math.round(percentX * (data.length - 1));
      const clampedIndex = Math.max(0, Math.min(data.length - 1, closestIndex));

      // Calculate pixel positions for tooltip and dot
      const dotX = (getX(clampedIndex) / width) * rect.width;
      const dotY = (getY(getValue(data[clampedIndex])) / height) * rect.height;

      setHoveredIndex(clampedIndex);
      setTooltipPos({ x: dotX, y: dotY });
    };

    const handleMouseLeave = () => {
      setHoveredIndex(null);
    };

    // Calculate end dot position in percentages
    const endDotLeft = (getX(lastIndex) / width) * 100;
    const endDotTop = (getY(getValue(data[lastIndex])) / height) * 100;

    // Find month boundaries for period markers
    const monthBoundaries: { index: number; prevMonth: string; nextMonth: string }[] = [];
    for (let i = 1; i < data.length; i++) {
      const prevDate = new Date(data[i - 1].date);
      const currDate = new Date(data[i].date);
      if (prevDate.getMonth() !== currDate.getMonth()) {
        monthBoundaries.push({
          index: i,
          prevMonth: prevDate.toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' }),
          nextMonth: currDate.toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' })
        });
      }
    }

    // Find min and max values for markers
    let maxIndex = 0;
    let minIndex = 0;
    let maxValue = values[0];
    let minValue = values[0];
    for (let i = 1; i < values.length; i++) {
      if (values[i] > maxValue) {
        maxValue = values[i];
        maxIndex = i;
      }
      if (values[i] < minValue) {
        minValue = values[i];
        minIndex = i;
      }
    }

    // Calculate positions for min/max markers (in percentages)
    const maxMarkerLeft = (getX(maxIndex) / width) * 100;
    const maxMarkerTop = (getY(maxValue) / height) * 100;
    const minMarkerLeft = (getX(minIndex) / width) * 100;
    const minMarkerTop = (getY(minValue) / height) * 100;

    return (
      <div
        className="dashboard-chart-wrapper"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <svg
          ref={svgRef}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          className="dashboard-chart-svg"
        >
          <defs>
            {/* Horizontal color gradient */}
            <linearGradient id={`${gradientId}-colors`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FF6B4A" />
              <stop offset="50%" stopColor="#E94B6C" />
              <stop offset="100%" stopColor="#8E3A9A" />
            </linearGradient>
            {/* Vertical fade mask */}
            <linearGradient id={`${gradientId}-fade`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="white" stopOpacity="0.4" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
            <mask id={`${gradientId}-mask`}>
              <rect x="0" y="0" width="100%" height="100%" fill={`url(#${gradientId}-fade)`} />
            </mask>
            {/* Stroke gradient */}
            <linearGradient id={`${gradientId}-stroke`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FF6B4A" />
              <stop offset="50%" stopColor="#E94B6C" />
              <stop offset="100%" stopColor="#8E3A9A" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#${gradientId}-colors)`} mask={`url(#${gradientId}-mask)`} />
          <path
            d={smoothPath}
            fill="none"
            stroke={`url(#${gradientId}-stroke)`}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Average line */}
          <line
            x1={paddingX}
            y1={avgY}
            x2={width - paddingX}
            y2={avgY}
            stroke="rgba(142, 58, 154, 0.35)"
            strokeWidth="1"
            strokeDasharray="2,3"
          />
          {/* Month boundary lines in SVG */}
          {monthBoundaries.map((boundary, idx) => {
            const x = (getX(boundary.index - 1) + getX(boundary.index)) / 2;
            return (
              <line
                key={idx}
                x1={x}
                y1={paddingY}
                x2={x}
                y2={height}
                stroke="rgba(0,0,0,0.1)"
                strokeWidth="1"
                strokeDasharray="4,4"
              />
            );
          })}
        </svg>

        {/* Average value label (HTML) */}
        <div
          className="avg-marker"
          style={{ top: `${(avgY / height) * 100}%` }}
        >
          <span className="avg-label">{t('dashboard.avg')} {formatAvgValue(avg)}</span>
        </div>

        {/* Month boundary labels (HTML) */}
        {monthBoundaries.map((boundary, idx) => {
          const xPercent = ((getX(boundary.index - 1) + getX(boundary.index)) / 2 / width) * 100;
          return (
            <div key={idx} className="month-boundary" style={{ left: `${xPercent}%` }}>
              <span className="month-label prev">{boundary.prevMonth}</span>
              <span className="month-label next">{boundary.nextMonth}</span>
            </div>
          );
        })}

        {/* Max value marker */}
        {hoveredIndex !== maxIndex && (
          <div
            className="value-marker max-marker"
            style={{
              left: `${maxMarkerLeft}%`,
              top: `${maxMarkerTop}%`
            }}
          >
            <div className="value-marker-dot"></div>
            <div className="value-marker-label">{formatTooltipValue(maxValue)}</div>
          </div>
        )}

        {/* Min value marker */}
        {hoveredIndex !== minIndex && (
          <div
            className="value-marker min-marker"
            style={{
              left: `${minMarkerLeft}%`,
              top: `${minMarkerTop}%`
            }}
          >
            <div className="value-marker-dot"></div>
            <div className="value-marker-label">{formatTooltipValue(minValue)}</div>
          </div>
        )}

        {/* HTML Hover dot */}
        {hoveredIndex !== null && (
          <div
            className="chart-dot hover-dot"
            style={{
              left: tooltipPos.x,
              top: tooltipPos.y,
              backgroundColor: strokeColor
            }}
          >
            <div className="chart-dot-inner"></div>
          </div>
        )}

        {/* HTML End dot */}
        {hoveredIndex !== lastIndex && (
          <div
            className="chart-dot end-dot"
            style={{
              left: `${endDotLeft}%`,
              top: `${endDotTop}%`,
              backgroundColor: strokeColorEnd
            }}
          />
        )}

        {/* HTML Tooltip */}
        {hoveredIndex !== null && (
          <div
            className="chart-tooltip show"
            style={{
              left: tooltipPos.x,
              top: tooltipPos.y - 110
            }}
          >
            <div className="chart-tooltip-date">{formatTooltipDate(data[hoveredIndex].date)}</div>
            <div className="chart-tooltip-value">{formatTooltipValue(getValue(data[hoveredIndex]))} {metricLabels[metric]}</div>
            {getChange(hoveredIndex) !== null && (
              <div className={`chart-tooltip-change ${getChange(hoveredIndex)! >= 0 ? 'positive' : 'negative'}`}>
                {getChange(hoveredIndex)! >= 0 ? '+' : ''}{getChange(hoveredIndex)!.toFixed(1)}% {t('dashboard.vsPrevDay')}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'regional_admin' || user?.role === 'manager';
  const spendChange = calculateChange(stats.spend, stats.spendPrev);
  const campaignsChange = calculateChange(stats.campaignsCount, stats.campaignsCountPrev);
  const conversionsChange = calculateChange(stats.conversions, stats.conversionsPrev);
  const clicksChange = calculateChange(stats.clicks, stats.clicksPrev);
  const earnChange = calculateChange(stats.earn || 0, stats.earnPrev || 0);
  const profitChange = calculateChange(stats.profit || 0, stats.profitPrev || 0);
  const clientsChange = calculateChange(stats.clientsCount || 0, stats.clientsCountPrev || 0);

  // Loading skeleton component
  const SkeletonCard = () => (
    <div className="stat-card skeleton">
      <div className="skeleton-line skeleton-label"></div>
      <div className="skeleton-line skeleton-value"></div>
      <div className="skeleton-line skeleton-comparison"></div>
    </div>
  );

  const SkeletonCampaignCard = () => (
    <div className="active-campaign-card skeleton">
      <div className="skeleton-line skeleton-title"></div>
      <div className="skeleton-line skeleton-subtitle"></div>
      <div className="skeleton-line skeleton-progress"></div>
      <div className="skeleton-metrics">
        <div className="skeleton-line skeleton-metric"></div>
        <div className="skeleton-line skeleton-metric"></div>
        <div className="skeleton-line skeleton-metric"></div>
      </div>
    </div>
  );

  const SkeletonChart = () => (
    <div className="performance-card skeleton">
      <div className="skeleton-chart"></div>
    </div>
  );

  if (loading) {
    return (
      <>
        <header className="header">
          <div className="header-title">
            <h1>{t('dashboard.title')}</h1>
            <p className="header-subtitle">{t('dashboard.subtitle')}</p>
          </div>
        </header>

        <div className="stats-grid">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>

        <div className="dashboard-grid">
          <div className="active-campaigns-section">
            <h2 className="section-title">{t('dashboard.activeCampaigns')}</h2>
            <div className="campaigns-list">
              <SkeletonCampaignCard />
              <SkeletonCampaignCard />
            </div>
          </div>
          <div className="chart-section">
            <h2 className="section-title">{t('dashboard.trends')}</h2>
            <SkeletonChart />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <header className="header">
        <div className="header-title">
          <h1>{t('dashboard.title')}</h1>
          <p className="header-subtitle">{t('dashboard.subtitle')}</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/reports')}>
            {getBranding().edition !== 'sense'
              ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 3v12M7 10l5 5 5-5M5 21h14"/>
                </svg>
              )
              : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1s-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                </svg>
              )}
            {t('dashboard.exportReport')}
          </button>
          {!isAnalyst && <NewCampaignButton />}
          {!isAnalyst && !isAllClients && selectedAdvertiser?.has_external_integration && <SenseMaxButton />}
        </div>
      </header>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">{t('dashboard.spend')}</span>
            <span className={`stat-badge ${spendChange >= 0 ? 'positive' : 'negative'}`}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                <path d={spendChange >= 0 ? "M7 14l5-5 5 5z" : "M7 10l5 5 5-5z"}/>
              </svg>
              {Math.abs(spendChange).toFixed(1)}%
            </span>
          </div>
          <div className="stat-value"><CurrencyValue value={stats.spend} large currency={currency} /></div>
          <div className="stat-comparison">{t('dashboard.vsLastMonth', { value: formatCurrency(stats.spendPrev, currency) })}</div>
        </div>

        {isAdmin ? (
          <>
            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-label">{t('dashboard.earn')}</span>
                <span className={`stat-badge ${earnChange >= 0 ? 'positive' : 'negative'}`}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <path d={earnChange >= 0 ? "M7 14l5-5 5 5z" : "M7 10l5 5 5-5z"}/>
                  </svg>
                  {Math.abs(earnChange).toFixed(1)}%
                </span>
              </div>
              <div className="stat-value"><CurrencyValue value={stats.earn || 0} large currency={currency} /></div>
              <div className="stat-comparison">{t('dashboard.vsLastMonth', { value: formatCurrency(stats.earnPrev || 0, currency) })}</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-label">{t('dashboard.profit')}</span>
                <span className={`stat-badge ${profitChange >= 0 ? 'positive' : 'negative'}`}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <path d={profitChange >= 0 ? "M7 14l5-5 5 5z" : "M7 10l5 5 5-5z"}/>
                  </svg>
                  {Math.abs(profitChange).toFixed(1)}%
                </span>
              </div>
              <div className="stat-value"><CurrencyValue value={stats.profit || 0} large currency={currency} /></div>
              <div className="stat-comparison">{t('dashboard.vsLastMonth', { value: formatCurrency(stats.profitPrev || 0, currency) })}</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-label">{isAllClients ? t('dashboard.activeClients') : t('dashboard.campaigns')}</span>
                <span className={`stat-badge ${(isAllClients ? clientsChange : campaignsChange) >= 0 ? 'positive' : 'negative'}`}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <path d={(isAllClients ? clientsChange : campaignsChange) >= 0 ? "M7 14l5-5 5 5z" : "M7 10l5 5 5-5z"}/>
                  </svg>
                  {Math.abs(isAllClients ? clientsChange : campaignsChange).toFixed(1)}%
                </span>
              </div>
              <div className="stat-value">{isAllClients ? (stats.clientsCount || 0) : (stats.campaignsCount || 0)}</div>
              <div className="stat-comparison">vs {isAllClients ? (stats.clientsCountPrev || 0) : (stats.campaignsCountPrev || 0)} last month</div>
            </div>
          </>
        ) : (
          <>
            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-label">{t('dashboard.campaigns')}</span>
                <span className={`stat-badge ${campaignsChange >= 0 ? 'positive' : 'negative'}`}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <path d={campaignsChange >= 0 ? "M7 14l5-5 5 5z" : "M7 10l5 5 5-5z"}/>
                  </svg>
                  {Math.abs(campaignsChange).toFixed(1)}%
                </span>
              </div>
              <div className="stat-value">{stats.campaignsCount}</div>
              <div className="stat-comparison">vs {stats.campaignsCountPrev} last month</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-label">{t('dashboard.conversions')}</span>
                <span className={`stat-badge ${conversionsChange >= 0 ? 'positive' : 'negative'}`}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <path d={conversionsChange >= 0 ? "M7 14l5-5 5 5z" : "M7 10l5 5 5-5z"}/>
                  </svg>
                  {Math.abs(conversionsChange).toFixed(1)}%
                </span>
              </div>
              <div className="stat-value">{stats.conversions}</div>
              <div className="stat-comparison">vs {stats.conversionsPrev} last month</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <span className="stat-label">{t('dashboard.clicks')}</span>
                <span className={`stat-badge ${clicksChange >= 0 ? 'positive' : 'negative'}`}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <path d={clicksChange >= 0 ? "M7 14l5-5 5 5z" : "M7 10l5 5 5-5z"}/>
                  </svg>
                  {Math.abs(clicksChange).toFixed(1)}%
                </span>
              </div>
              <div className="stat-value">{stats.clicks.toLocaleString('ru-RU')}</div>
              <div className="stat-comparison">vs {stats.clicksPrev.toLocaleString('ru-RU')} last month</div>
            </div>
          </>
        )}
      </div>

      {/* Active Campaigns */}
      <section className="campaigns-section">
        <div className="section-header">
          <h2 className="section-title">{t('dashboard.activeCampaigns')}</h2>
          <button className="btn btn-secondary" onClick={() => navigate('/campaigns')}>{t('dashboard.viewAll')}</button>
        </div>

        <div className="campaign-grid">
          {activeCampaigns.map(campaign => {
            const budgetPercent = campaign.budget > 0 ? (campaign.spend / campaign.budget) * 100 : 0;

            return (
              <div key={campaign.id} className="campaign-card">
                <div className="campaign-header">
                  <div className="campaign-info">
                    <h3>{cleanTimestampSuffix(campaign.name)}</h3>
                    <div className="campaign-status">
                      {getBranding().edition !== 'sense' ? (
                        <>
                          <span className="live-pill"><span className="dot"></span>{t('dashboard.live')}</span>
                          {campaign.daysRemaining > 0 && (
                            <span className="campaign-meta">{t('dashboard.daysRemaining', { count: campaign.daysRemaining })}</span>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="status-dot live"></span>
                          Live {campaign.daysRemaining > 0 ? `• ${t('dashboard.daysRemaining', { count: campaign.daysRemaining })}` : ''}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="campaign-budget">
                    <div className="budget-label">{campaign.has_external_data ? t('dashboard.spend') : t('dashboard.budget')}</div>
                    {campaign.has_external_data ? (
                      <div className="budget-value"><CurrencyValue value={campaign.spend} currency={campaign.budget_currency || currency} short /></div>
                    ) : (
                      <>
                        <div className="budget-value"><CurrencyValue value={campaign.spend} currency={campaign.budget_currency || currency} short /> / <CurrencyValue value={campaign.budget} currency={campaign.budget_currency || currency} short /></div>
                        <div className="budget-bar">
                          <div className="budget-fill" style={{ width: `${Math.min(budgetPercent, 100)}%` }}></div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="campaign-metrics">
                  <div className="metric">
                    <div className="metric-value">{formatNumber(campaign.impressions)}</div>
                    <div className="metric-label">{t('dashboard.impressions')}</div>
                  </div>
                  <div className="metric">
                    <div className="metric-value">{campaign.clicks.toLocaleString('ru-RU')}</div>
                    <div className="metric-label">{t('dashboard.clicks')}</div>
                  </div>
                  <div className="metric">
                    {campaign.conversions > 0 ? (
                      <>
                        <div className="metric-value">{campaign.conversions.toLocaleString('ru-RU')}</div>
                        <div className="metric-label">{t('dashboard.conversions')}</div>
                      </>
                    ) : (
                      <>
                        <div className="metric-value">{campaign.ctr.toFixed(2)}%</div>
                        <div className="metric-label">{t('dashboard.ctr')}</div>
                      </>
                    )}
                  </div>
                  <div className="metric">
                    <div className="metric-value">{formatCurrency(campaign.cpc, campaign.budget_currency || currency)}</div>
                    <div className="metric-label">{t('dashboard.cpc')}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Analytics Chart — hidden when no data */}
      {trendData.length > 0 && <section className="analytics-section">
        <div className="analytics-header">
          <h2 className="section-title">{t('dashboard.trends')}</h2>
          <div className="chart-tabs">
            <button
              className={`chart-tab ${selectedMetric === 'spend' ? 'active' : ''}`}
              onClick={() => setSelectedMetric('spend')}
            >
              {t('dashboard.spend')}
            </button>
            <button
              className={`chart-tab ${selectedMetric === 'impressions' ? 'active' : ''}`}
              onClick={() => setSelectedMetric('impressions')}
            >
              {t('dashboard.impressions')}
            </button>
            <button
              className={`chart-tab ${selectedMetric === 'clicks' ? 'active' : ''}`}
              onClick={() => setSelectedMetric('clicks')}
            >
              {t('dashboard.clicks')}
            </button>
          </div>
        </div>
        <div className="chart-placeholder">
          <DashboardChart data={trendData} metric={selectedMetric} />
        </div>
      </section>}

    </>
  );
}

export default Dashboard;
